import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import type { UnlinkedEvidenceItem } from '../../../types/custody-actions'

interface UnlinkedEvidenceDialogProps {
  open: boolean
  unlinkedItems: UnlinkedEvidenceItem[]
  onCancel: () => void
  onGenerateCr: (notes: string) => void
  generating: boolean
}

function UnlinkedItemsTable({ items }: { items: UnlinkedEvidenceItem[] }): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[var(--border)]">
          <th className="pb-2 text-left text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
            {t('table.evidenceNumber')}
          </th>
          <th className="pb-2 text-left text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
            {t('table.infrastructure')}
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.evidenceNumberId} className="border-b border-[var(--border)] last:border-0">
            <td className="py-2 text-xs font-[var(--font-mono)] text-[var(--text)]">
              {item.evidenceNumber}
            </td>
            <td className="py-2 text-xs text-[var(--text-muted)]">{item.endpointHostname}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DialogContent({
  unlinkedItems,
  notes,
  onNotesChange,
}: {
  unlinkedItems: UnlinkedEvidenceItem[]
  notes: string
  onNotesChange: (val: string) => void
}): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="flex-1 space-y-4 overflow-y-auto p-6">
      <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm text-amber-800">{t('custodyActions.linkageCheck.description')}</p>
          <p className="text-sm text-amber-700">{t('custodyActions.linkageCheck.prompt')}</p>
        </div>
      </div>
      <UnlinkedItemsTable items={unlinkedItems} />
      <div>
        <label htmlFor="cr-notes" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.linkageCheck.notesLabel')}
        </label>
        <textarea
          id="cr-notes"
          value={notes}
          onChange={(ev) => {
            onNotesChange(ev.target.value)
          }}
          placeholder={t('custodyActions.linkageCheck.notesPlaceholder')}
          rows={3}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
    </div>
  )
}

function DialogFooter({
  onCancel,
  onGenerate,
  generating,
  notesEmpty,
}: {
  onCancel: () => void
  onGenerate: () => void
  generating: boolean
  notesEmpty: boolean
}): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
      <button
        type="button"
        onClick={onCancel}
        disabled={generating}
        className="rounded border border-[var(--border)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text)] uppercase hover:opacity-80 disabled:opacity-50"
      >
        {t('custodyActions.linkageCheck.cancel')}
      </button>
      <button
        type="button"
        onClick={onGenerate}
        disabled={generating || notesEmpty}
        className="rounded bg-[var(--accent)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-white uppercase hover:opacity-90 disabled:opacity-50"
      >
        {generating
          ? t('custodyActions.linkageCheck.generating')
          : t('custodyActions.linkageCheck.generate')}
      </button>
    </div>
  )
}

export function UnlinkedEvidenceDialog({
  open,
  unlinkedItems,
  onCancel,
  onGenerateCr,
  generating,
}: UnlinkedEvidenceDialogProps): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  const [notes, setNotes] = useState('')

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
        <div className="border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-[var(--font-heading)] font-semibold text-[var(--text)]">
            {t('custodyActions.linkageCheck.title')}
          </h2>
        </div>
        <DialogContent unlinkedItems={unlinkedItems} notes={notes} onNotesChange={setNotes} />
        <DialogFooter
          onCancel={onCancel}
          onGenerate={() => {
            onGenerateCr(notes)
          }}
          generating={generating}
          notesEmpty={notes.trim().length === 0}
        />
      </div>
    </div>
  )
}
